import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { Table } from 'react-bootstrap';

import { SECTIONS } from 'components/upb_viewer/constants';
import {
  BG_1,
  BG_2,
  BORDER_1,
  BORDER_2,
  ERROR_BG,
  ERROR_BORDER,
  ERROR_TEXT,
  FG_1,
  FG_2,
  FG_3,
  FONT_BODY,
  PRIMARY_COLOR,
  RADIUS_MD,
  SUCCESS_BG,
  SUCCESS_BORDER,
  SUCCESS_TEXT,
} from 'components/upb_viewer_beta/css/betaTokens';
import BetaNutritionFactsLabel from 'components/upb_viewer_beta/pdp/BetaNutritionFactsLabel';
import BetaPdpSection from 'components/upb_viewer_beta/pdp/BetaPdpSection';
import BetaProductGallery from 'components/upb_viewer_beta/pdp/BetaProductGallery';

const isNil = v => v === null || v === undefined || v === '';

// Hoisted so useMemos depend only on `sections` — silences exhaustive-deps.
const findSectionByName = (sections, name) => (sections || []).find(s => s && s.name === name);
const sectionAttrs = (sections, name) => findSectionByName(sections, name)?.attributes || [];

// Beta-styled rebuild of the Product Details tab. Data comes from
// `product.sections`, the same shape `UPBProductDetailsPage` consumes from
// `/global-product-pdp`. Sections with no attributes are skipped.
function BetaProductDetailsTab({ product }) {
  const sections = product?.sections;

  const overviewAttrs = useMemo(() => sectionAttrs(sections, SECTIONS.OVERVIEW), [sections]);
  const descriptionAttrs = useMemo(() => sectionAttrs(sections, SECTIONS.DESCRIPTION), [sections]);
  const weightsAttrs = useMemo(
    () => sectionAttrs(sections, SECTIONS.WEIGHTS_AND_DIMENSIONS),
    [sections],
  );
  const specsAttrs = useMemo(() => sectionAttrs(sections, SECTIONS.SPECIFICATIONS), [sections]);
  const topOffAttrs = useMemo(() => sectionAttrs(sections, SECTIONS.TOP_OFF), [sections]);
  const storageAttrs = useMemo(
    () => sectionAttrs(sections, SECTIONS.STORAGE_INSTRUCTIONS),
    [sections],
  );
  const tipsAttrs = useMemo(() => sectionAttrs(sections, SECTIONS.TIPS_AND_HANDLING), [sections]);
  const prepAttrs = useMemo(
    () => sectionAttrs(sections, SECTIONS.PREPARATION_INSTRUCTIONS),
    [sections],
  );
  const servingAttrs = useMemo(
    () => sectionAttrs(sections, SECTIONS.SERVING_SUGGESTIONS),
    [sections],
  );
  const identifiersAttrs = useMemo(
    () => sectionAttrs(sections, SECTIONS.PRODUCT_IDENTIFIERS),
    [sections],
  );
  const nutritionFactsAttrs = useMemo(
    () => sectionAttrs(sections, SECTIONS.NUTRITION_FACTS),
    [sections],
  );

  // Ingredients statement lives inside the INGREDIENTS section as a single
  // attribute named `ingredients_statement`.
  const ingredients = useMemo(() => {
    const ing = findSectionByName(sections, SECTIONS.INGREDIENTS);
    return ing?.attributes?.find(a => a.attribute_name === 'ingredients_statement')
      ?.attribute_value;
  }, [sections]);

  // Allergens come grouped by display name (Contains / Free from / Not
  // intentionally included). Claims are flat string values.
  const allergens = useMemo(() => {
    const a = sectionAttrs(sections, SECTIONS.ALLERGENS);
    return Array.isArray(a) ? a : [];
  }, [sections]);

  const claims = useMemo(() => {
    const c = sectionAttrs(sections, SECTIONS.CLAIMS);
    return Array.isArray(c) ? c.map(x => x.attribute_value).filter(Boolean) : [];
  }, [sections]);

  // Manufacturer & brand — always render the six slots from the mockup so the
  // 2-column grid stays visually balanced. Missing values show "—" rather than
  // collapsing the cell. Codify ID is the global_product_id (Codify is our
  // internal name for the catalog); "Other identifier" falls back to the first
  // vendor SKU from PRODUCT_IDENTIFIERS, which is a list of
  // `{display_name: vendor_name, attribute_value: vendor_sku}` rows.
  //
  // UPC fallback: GlobalProduct.upc is sparsely populated in our catalog —
  // most rows only carry GTIN. For US grocery products GTIN-12 IS the UPC-A
  // (same barcode), so when the explicit UPC column is empty we show the GTIN
  // value as UPC. This matches what the existing UPB viewer and supplier
  // feeds treat as equivalent.
  const manufacturerCells = useMemo(() => {
    const firstVendorSku = identifiersAttrs.find(a => !isNil(a?.attribute_value))?.attribute_value;
    const dash = '—';
    const upcDisplay = product?.upc || product?.gtin || dash;
    return [
      { k: 'Manufacturer', v: product?.manufacturer?.name || dash },
      { k: 'Brand', v: product?.brand?.name || product?.propagated_brand || dash },
      { k: 'GTIN', v: product?.gtin || dash },
      { k: 'UPC', v: upcDisplay },
      { k: 'Other identifier', v: firstVendorSku || dash },
      { k: 'Codify ID', v: product?.id != null ? String(product.id) : dash },
    ];
  }, [product, identifiersAttrs]);

  // Always show the section — even on sparse products the layout slot stays so
  // the page matches the mockup. The product id itself is always present, so
  // there is no truly-empty case.
  const hasMfrBrand = !!product?.id || manufacturerCells.length > 0;
  const hasOverview = overviewAttrs.length > 0;
  const hasDescription = descriptionAttrs.length > 0;
  const hasNutrition =
    !!ingredients || allergens.length > 0 || claims.length > 0 || nutritionFactsAttrs.length > 0;
  const hasWeights = weightsAttrs.length > 0;
  const hasSpecs = specsAttrs.length + topOffAttrs.length > 0;
  const tipsText = pickTipsText(tipsAttrs);
  const prepText = pickTipsText(prepAttrs);
  const servingText = pickTipsText(servingAttrs);
  const hasTips = storageAttrs.length > 0 || !!tipsText || !!prepText || !!servingText;

  return (
    <div className={css(styles.tab)}>
      {/* Top row — gallery + right column */}
      <div className={css(styles.topRow)}>
        <BetaProductGallery images={product?.product_images} />

        <div className={css(styles.content)}>
          {hasMfrBrand && (
            <BetaPdpSection title="Manufacturer & brand">
              <KvGrid cells={manufacturerCells} />
            </BetaPdpSection>
          )}

          {hasOverview && (
            <BetaPdpSection title="Product overview">
              <KvGrid cells={attrsToCells(overviewAttrs)} />
            </BetaPdpSection>
          )}

          {hasDescription && (
            <BetaPdpSection title="Description">
              <DescriptionBody attributes={descriptionAttrs} />
            </BetaPdpSection>
          )}

          {hasNutrition && (
            <BetaPdpSection title="Nutrition">
              <NutritionBody
                ingredients={ingredients}
                allergens={allergens}
                claims={claims}
                nutritionFacts={nutritionFactsAttrs}
              />
            </BetaPdpSection>
          )}
        </div>
      </div>

      {/* Below-the-fold full-width sections */}
      {hasWeights && (
        <BetaPdpSection title="Weights + dimensions">
          <KvTable rows={attrsToCells(weightsAttrs)} />
        </BetaPdpSection>
      )}

      {hasSpecs && (
        <BetaPdpSection title="Specifications">
          <KvTable rows={attrsToCells([...specsAttrs, ...topOffAttrs])} />
        </BetaPdpSection>
      )}

      {hasTips && (
        <BetaPdpSection title="Tips + handling">
          <TipsBody
            tipsText={tipsText}
            storageAttrs={storageAttrs}
            prepText={prepText}
            servingText={servingText}
          />
        </BetaPdpSection>
      )}
    </div>
  );
}

BetaProductDetailsTab.propTypes = {
  product: PropTypes.object,
};

BetaProductDetailsTab.defaultProps = {
  product: null,
};

/* ─── Helpers ─────────────────────────────────────────────────────────── */

// GDSN unit codes coming out of the supplier feed → friendly display labels.
// The data stores raw GDSN UN/ECE codes (INH, LBR, FOT3, …); the mockup uses
// the customary short forms (in, lb, ft³, …). Apply on any value that may
// contain a UOM token before showing it.
const UOM_DISPLAY_MAP = {
  // length
  INH: 'in',
  INCH: 'in',
  IN: 'in',
  FOT: 'ft',
  FT: 'ft',
  YRD: 'yd',
  MTR: 'm',
  CMT: 'cm',
  MMT: 'mm',
  // weight
  LBR: 'lb',
  LB: 'lb',
  LBS: 'lb',
  POUND: 'lb',
  KGM: 'kg',
  GRM: 'g',
  MGM: 'mg',
  OZA: 'oz',
  OZ: 'oz',
  // volume
  FOZ: 'fl oz',
  GLL: 'gal',
  GAL: 'gal',
  QT: 'qt',
  PT: 'pt',
  LTR: 'L',
  MLT: 'mL',
  ML: 'mL',
  FT3: 'ft³',
  FOT3: 'ft³',
  CMQ: 'cm³',
  MTQ: 'm³',
  // temperature — the storage_temperature_*_f_or_c UOM column literally
  // stores "F" or "C", so we have to special-case the single-letter form as
  // well as the GDSN long codes (FAH / CEL).
  F: '°F',
  C: '°C',
  FAH: '°F',
  CEL: '°C',
  // duration
  DAY: 'days',
  DAYS: 'days',
  MON: 'months',
  ANN: 'years',
};

// Attribute-specific value post-processing. Some attributes don't carry a
// companion UOM in the data model (the GDSN field is always one unit by
// convention) — we append it here so the value reads naturally.
const ATTR_VALUE_SUFFIX = {
  // GDSN `lifespanFromTimeOfProduction` is always days.
  shelf_life_from_production: ' days',
  shelf_life_from_arrival: ' days',
};

function applyAttrSuffix(attributeName, value) {
  if (!attributeName || typeof value !== 'string') return value;
  const suffix = ATTR_VALUE_SUFFIX[attributeName];
  if (!suffix) return value;
  // Avoid double-appending if a unit is already present.
  if (/\b(day|days|d|month|months|year|years)\b/i.test(value)) return value;
  return value.trim() + suffix;
}

// Token-wise replacement — split on whitespace so we only swap whole-token
// UOM matches (avoids munging e.g. "FOTGRA" or "MINTeam"). Letters stay
// case-sensitive on lookup since GDSN codes are uppercase by convention.
function normalizeUomTokens(text) {
  if (typeof text !== 'string') return text;
  return text
    .split(/(\s+)/) // keep whitespace tokens so spacing is preserved
    .map(tok => UOM_DISPLAY_MAP[tok] || tok)
    .join('');
}

// Most sections have a flat `attributes: [{display_name, attribute_value}]`.
// Map to the {k, v} shape the kv primitives consume, skipping empty values
// so the layout doesn't render half-empty cells. Concatenated dimension /
// weight strings get UOM normalization + " x " → " × " for the multiply
// glyph the mockup uses.
function attrsToCells(attributes) {
  return (attributes || [])
    .map(a => {
      let v = a.attribute_value;
      if (typeof v === 'string') {
        v = normalizeUomTokens(v);
        // Concatenated dimensions use a plain ASCII "x" — swap in × so the
        // row reads "12.625 in × 10.188 in × 12.500 in" like the mockup.
        if (/\d\s*x\s*\d/.test(v)) v = v.replace(/\s*x\s*/g, ' × ');
        // Append per-attribute conventional units (e.g. " days" for shelf
        // life) when the data model has no companion UOM column.
        v = applyAttrSuffix(a.attribute_name, v);
      }
      return {
        k: a.display_name || a.attribute_name || '',
        v,
      };
    })
    .filter(c => !isNil(c.v) && !!c.k);
}

// Tips / preparation / serving sections all use the same single-attribute
// shape — pick the first non-empty `attribute_value`.
function pickTipsText(attributes) {
  if (!Array.isArray(attributes)) return '';
  const first = attributes.find(a => !isNil(a?.attribute_value));
  return first?.attribute_value || '';
}

/* ─── Internal renderers ──────────────────────────────────────────────── */

function KvGrid({ cells }) {
  return (
    <div className={css(kvStyles.grid)}>
      {cells.map((c, i) => (
        <div key={i} className={css(kvStyles.cell)}>
          <div className={css(kvStyles.k)}>{c.k}</div>
          <div className={css(kvStyles.v)}>{c.v}</div>
        </div>
      ))}
    </div>
  );
}

KvGrid.propTypes = { cells: PropTypes.array.isRequired };

function KvTable({ rows }) {
  return (
    <Table className={css(kvStyles.table)}>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className={css(i % 2 === 0 ? kvStyles.rowOdd : kvStyles.rowEven)}>
            <td className={css(kvStyles.tableK)}>{r.k}</td>
            <td className={css(kvStyles.tableV)}>{r.v}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

KvTable.propTypes = { rows: PropTypes.array.isRequired };

function DescriptionBody({ attributes }) {
  // The DESCRIPTION section is usually a single long text attribute, but it
  // may carry several feature-like rows. Render the first text block as a
  // paragraph and the rest (if any) as a feature bullet list — mirrors the
  // mockup's "Features and benefits" sub-block.
  const blocks = attributes.filter(a => !isNil(a.attribute_value));
  if (!blocks.length) return null;
  const [first, ...rest] = blocks;
  return (
    <>
      <p className={css(textStyles.paragraph)}>{first.attribute_value}</p>
      {rest.length > 0 && (
        <>
          <h4 className={css(textStyles.subhead)}>
            {first.display_name === 'Features and benefits'
              ? 'More details'
              : 'Features and benefits'}
          </h4>
          <ul className={css(textStyles.featureList)}>
            {rest.map((a, i) => (
              <li key={i} className={css(textStyles.featureItem)}>
                {a.attribute_value}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

DescriptionBody.propTypes = { attributes: PropTypes.array.isRequired };

function NutritionBody({ ingredients, allergens, claims, nutritionFacts }) {
  // Allergens are grouped — each group has `displayName` (e.g. "Contains")
  // and a `values` array of {value, icon}. Tone the chip variant on the
  // group name so positive ("Free from") stays green and presence
  // ("Contains") stays red.
  const variantFor = group => {
    const lower = (group || '').toLowerCase();
    if (lower.includes('contain')) return 'contains';
    if (lower.includes('free')) return 'free';
    return 'subtle';
  };

  return (
    <>
      {ingredients && (
        <>
          <h4 className={css(textStyles.subheadTight)}>Ingredients</h4>
          <p className={css(textStyles.paragraph)}>{ingredients}</p>
        </>
      )}
      {allergens.length > 0 && (
        <>
          <h4 className={css(textStyles.subhead)}>Allergens</h4>
          {allergens.map(group => {
            const values = Array.isArray(group.values) ? group.values : [];
            if (!values.length) return null;
            const variant = variantFor(group.displayName);
            return (
              <div key={group.displayName} className={css(chipStyles.row)}>
                <div className={css(chipStyles.label)}>{group.displayName}</div>
                <div className={css(chipStyles.chips)}>
                  {values.map((tag, i) => (
                    <span
                      key={i}
                      className={css(
                        chipStyles.chip,
                        variant === 'contains' && chipStyles.chipContains,
                        variant === 'free' && chipStyles.chipFree,
                        variant === 'subtle' && chipStyles.chipSubtle,
                      )}
                    >
                      {tag.value || tag}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}
      {claims.length > 0 && (
        <>
          <h4 className={css(textStyles.subhead)}>Claims</h4>
          <div className={css(chipStyles.row)}>
            <div className={css(chipStyles.chips)}>
              {claims.map((c, i) => (
                <span key={i} className={css(chipStyles.chip)}>
                  {c}
                </span>
              ))}
            </div>
          </div>
        </>
      )}
      {nutritionFacts.length > 0 && (
        <>
          <h4 className={css(textStyles.subhead)}>Nutrition facts</h4>
          <BetaNutritionFactsLabel attributes={nutritionFacts} />
        </>
      )}
    </>
  );
}

NutritionBody.propTypes = {
  ingredients: PropTypes.string,
  allergens: PropTypes.array,
  claims: PropTypes.array,
  nutritionFacts: PropTypes.array,
};

NutritionBody.defaultProps = {
  ingredients: '',
  allergens: [],
  claims: [],
  nutritionFacts: [],
};

function TipsBody({ tipsText, storageAttrs, prepText, servingText }) {
  return (
    <>
      {tipsText && <p className={css(textStyles.paragraph)}>{tipsText}</p>}
      {storageAttrs.length > 0 && (
        <>
          <h4 className={css(textStyles.subhead)}>Storage instructions</h4>
          <KvTable rows={attrsToCells(storageAttrs)} />
        </>
      )}
      {prepText && (
        <>
          <h4 className={css(textStyles.subhead)}>Preparation instructions</h4>
          <p className={css(textStyles.paragraph)}>{prepText}</p>
        </>
      )}
      {servingText && (
        <>
          <h4 className={css(textStyles.subhead)}>Serving suggestions</h4>
          <p className={css(textStyles.paragraph)}>{servingText}</p>
        </>
      )}
    </>
  );
}

TipsBody.propTypes = {
  tipsText: PropTypes.string,
  storageAttrs: PropTypes.array,
  prepText: PropTypes.string,
  servingText: PropTypes.string,
};

TipsBody.defaultProps = {
  tipsText: '',
  storageAttrs: [],
  prepText: '',
  servingText: '',
};

/* ─── Styles ──────────────────────────────────────────────────────────── */

const styles = StyleSheet.create({
  tab: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    fontFamily: FONT_BODY,
  },
  topRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(320px, 440px) 1fr',
    gap: 24,
    alignItems: 'start',
    '@media (max-width: 1024px)': {
      gridTemplateColumns: '1fr',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    minWidth: 0,
  },
});

const kvStyles = StyleSheet.create({
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: 32,
    backgroundColor: BG_2,
    borderRadius: 8,
    marginTop: 16,
  },
  cell: {
    padding: '14px 16px',
    borderBottom: `1px solid ${BORDER_2}`,
    ':nth-last-child(-n+2)': {
      borderBottom: 'none',
    },
  },
  k: {
    fontFamily: FONT_BODY,
    fontWeight: 600,
    fontSize: 12,
    color: FG_3,
    marginBottom: 3,
  },
  v: {
    fontFamily: FONT_BODY,
    fontWeight: 600,
    fontSize: 14,
    color: FG_1,
    wordBreak: 'break-word',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    marginTop: 14,
    backgroundColor: BG_2,
    borderRadius: RADIUS_MD,
    overflow: 'hidden',
  },
  tableK: {
    padding: '12px 16px',
    borderBottom: `1px solid ${BORDER_2}`,
    fontFamily: FONT_BODY,
    fontSize: 14,
    fontWeight: 500,
    color: FG_3,
    width: 240,
  },
  tableV: {
    padding: '12px 16px',
    borderBottom: `1px solid ${BORDER_2}`,
    fontFamily: FONT_BODY,
    fontSize: 14,
    fontWeight: 600,
    color: FG_1,
    fontVariantNumeric: 'tabular-nums',
    wordBreak: 'break-word',
  },
  rowOdd: {
    backgroundColor: BG_2,
    ':last-child > td': {
      borderBottom: 'none',
    },
  },
  rowEven: {
    backgroundColor: BG_1,
    ':last-child > td': {
      borderBottom: 'none',
    },
  },
});

const textStyles = StyleSheet.create({
  paragraph: {
    fontFamily: FONT_BODY,
    fontSize: 14,
    lineHeight: 1.55,
    color: FG_2,
    margin: '12px 0',
  },
  subhead: {
    fontFamily: FONT_BODY,
    fontSize: 13,
    fontWeight: 700,
    color: FG_1,
    margin: '18px 0 8px',
  },
  subheadTight: {
    fontFamily: FONT_BODY,
    fontSize: 13,
    fontWeight: 700,
    color: FG_1,
    margin: '14px 0 6px',
  },
  featureList: {
    margin: '8px 0 0',
    padding: 0,
    listStyle: 'none',
  },
  featureItem: {
    position: 'relative',
    paddingLeft: 18,
    marginBottom: 6,
    fontFamily: FONT_BODY,
    fontSize: 14,
    color: FG_2,
    ':before': {
      content: '""',
      position: 'absolute',
      left: 2,
      top: 9,
      width: 6,
      height: 6,
      borderRadius: 9999,
      backgroundColor: PRIMARY_COLOR,
    },
  },
});

const chipStyles = StyleSheet.create({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    margin: '8px 0 16px',
    flexWrap: 'wrap',
  },
  label: {
    fontFamily: FONT_BODY,
    fontSize: 13,
    fontWeight: 600,
    color: FG_2,
    minWidth: 170,
    flexShrink: 0,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    flex: 1,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: `1px solid ${BORDER_1}`,
    borderRadius: 9999,
    padding: '5px 12px',
    backgroundColor: BG_1,
    fontFamily: FONT_BODY,
    fontSize: 12,
    fontWeight: 600,
    color: FG_1,
  },
  chipSubtle: {
    backgroundColor: BG_2,
    borderColor: BORDER_2,
    color: FG_2,
  },
  chipContains: {
    backgroundColor: ERROR_BG,
    borderColor: ERROR_BORDER,
    color: ERROR_TEXT,
  },
  chipFree: {
    backgroundColor: SUCCESS_BG,
    borderColor: SUCCESS_BORDER,
    color: SUCCESS_TEXT,
  },
});

export default BetaProductDetailsTab;
